/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Blocks 필드 클래스를 정의한다.
 *
 * @file /src/components/form/field/blocks/Blocks.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 6.
 */
import * as parent from '@components/form/field/Field';
import { File } from '@components/form/field/file/File';
import { Rootin } from '@rootin/Rootin';
import { Html } from '@utils/Html';
import { Dom } from '@utils/Dom';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 업로드 URL
     */
    url?: string;

    /**
     * 사용할 툴 목록 (미지정시 에디터 기본값을 사용한다.)
     */
    tools?: Rootin.Properties['tools'];

    /**
     * 이벤트 리스너
     */
    listeners?: Listeners<T>;
}

export class Blocks extends parent.Field {
    /**
     * 컴포넌트 확장타입
     */
    variant = 'blocks';

    /**
     * 업로드 URL
     */
    url: string;

    /**
     * Textarea DOM
     */
    $textarea: Dom<HTMLTextAreaElement> = null;

    /**
     * 위지윅 에디터 클래스
     */
    editor: Rootin.Editor = null;

    /**
     * 블록 에디터 필드 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Blocks> = null) {
        super(properties);

        this.url = properties?.url ?? File.url ?? '/';
    }

    /**
     * Textarea DOM 을 가져온다.
     *
     * @returns Textarea DOM
     */
    $getTextarea(): Dom<HTMLTextAreaElement> {
        if (this.$textarea == null) {
            this.$textarea = Html.create('textarea', { rows: '10' });
        }

        return this.$textarea;
    }

    /**
     * 에디터 클래스를 가져온다.
     *
     * @returns 에디터 클래스
     */
    getEditor(): Rootin.Editor {
        if (this.editor == null) {
            this.editor = new Rootin.Editor(this.$getTextarea(), {
                url: this.url,
                tools: this.properties?.tools,
            });
        }

        return this.editor;
    }

    /**
     * 필드값을 가져온다.
     *
     * @returns 필드값
     */
    async getValue(): Promise<{ [key: string]: any }[]> {
        if (this.getEditor() === null) {
            await this.getEditor().render();
        }

        return this.getEditor().getValue();
    }

    /**
     * 필드값을 지정한다.
     *
     * @param value - 값
     * @param is_init - 초기값여부
     */
    async setValue(value: { [key: string]: any }[], is_init: boolean = false): Promise<void> {
        await this.lockValue();

        if (this.getEditor() === null) {
            await this.getEditor().render();
        }

        this.getEditor().setValue(value);
        this.$getTextarea().setValue(value === null ? '' : JSON.stringify(value, null, 4), false);

        await this.releaseValue();
    }

    /**
     * 필드영역을 렌더링한다.
     */
    async renderField(): Promise<void> {
        this.$getContent().append(this.$getTextarea());
    }

    /**
     * 컴포넌트 렌더링이 완료된 이후 초기상태를 렌더링한다.
     */
    async renderState(): Promise<void> {
        await this.getEditor().render();
        this.$droppable = this.getEditor().root.body.$getElement();
        await super.renderState();
    }

    /**
     * 파일이 컴포넌트 영역으로 드래그되어 들어왔을 때 이벤트를 처리한다.
     *
     * @param is_droppable - 파일이 컴포넌트에 드롭가능한지 여부
     * @param is_enter - 드래그영역안에 들어왔는지 여부
     */
    async onFileDrag(is_droppable: boolean, is_enter: boolean | null): Promise<void> {}
}
